import { CustomCard } from '@/components';
import { PATH } from '@/constants';
import { LanguageStatus } from '@/constants/enum';
import { findLanguage } from '@/helpers';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import {
  useRemoveLanguageMutation,
  useSetDefaultLanguageMutation,
  useSetLanguageStatusMutation,
} from '@/redux/api/api.caller';
import { ILanguageList, ILanguageListItem } from '@/types/language';
import { ActionList, Badge, Button, IndexTable, Modal, Popover, Text, Tooltip } from '@shopify/polaris';
import { LockIcon, MenuHorizontalIcon, PlusIcon } from '@shopify/polaris-icons';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { getAddLanguageQuota } from '../languages.config';
import { handleMutationResult } from '../toastResult';
import AddLanguageModal from './modals/AddLanguageModal';

const STATUS_LABEL: Record<LanguageStatus, string> = {
  [LanguageStatus.Default]: 'Default',
  [LanguageStatus.Published]: 'Published',
  [LanguageStatus.Unpublished]: 'Unpublished',
};

const formatDate = (value: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const LanguageRow = ({
  item,
  index,
  publishDisabled,
}: {
  item: ILanguageListItem;
  index: number;
  publishDisabled: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useConfirmationNavigate();
  const [open, setOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removeLanguage, { isLoading: isRemoving }] = useRemoveLanguageMutation();
  const [setLanguageStatus] = useSetLanguageStatusMutation();
  const [setDefaultLanguage] = useSetDefaultLanguageMutation();
  const { language, status } = item;
  const isDefault = status === LanguageStatus.Default;
  const languageLabel = findLanguage(language)?.label || language;

  const run = (promise: any) => promise.then((res: any) => handleMutationResult(dispatch, res));

  const onConfirmRemove = () => {
    removeLanguage({ language }).then((res: any) => {
      if (handleMutationResult(dispatch, res)) setConfirmRemove(false);
    });
  };

  const actions = [
    isDefault
      ? { content: 'Edit text in Tracking page configuration →', onAction: () => navigate(PATH.TRACKING_CONFIGURATION_PAGE) }
      : { content: 'Edit translations', onAction: () => navigate(PATH.LANGUAGE_EDITOR.replace(':language', language)) },
    status === LanguageStatus.Published && {
      content: 'Unpublish',
      onAction: () => run(setLanguageStatus({ language, status: LanguageStatus.Unpublished })),
    },
    status === LanguageStatus.Unpublished && {
      content: 'Publish',
      disabled: publishDisabled,
      helpText: publishDisabled ? 'Plan limit reached — unpublish another or upgrade.' : undefined,
      onAction: () => run(setLanguageStatus({ language, status: LanguageStatus.Published })),
    },
    !isDefault && { content: 'Set as default', onAction: () => run(setDefaultLanguage({ language })) },
    !isDefault && { content: 'Remove', destructive: true, onAction: () => setConfirmRemove(true) },
  ].filter(Boolean) as {
    content: string;
    onAction: () => void;
    destructive?: boolean;
    disabled?: boolean;
    helpText?: string;
  }[];

  return (
    <IndexTable.Row id={language} position={index}>
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd" fontWeight="semibold">
          {languageLabel}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={isDefault ? 'info' : status === LanguageStatus.Published ? 'success' : undefined}>
          {STATUS_LABEL[status]}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{formatDate(item.updatedAt)}</IndexTable.Cell>
      <IndexTable.Cell>
        <Popover
          active={open}
          onClose={() => setOpen(false)}
          activator={
            <button className="Polaris-Button Polaris-Button--plain" type="button" onClick={() => setOpen((v) => !v)}>
              <MenuHorizontalIcon width={18} height={18} />
            </button>
          }
        >
          <ActionList
            items={actions.map((a) => ({
              content: a.content,
              destructive: a.destructive,
              disabled: a.disabled,
              helpText: a.helpText,
              onAction: () => {
                setOpen(false);
                a.onAction();
              },
            }))}
          />
        </Popover>
        <Modal
          open={confirmRemove}
          title="Remove language"
          onClose={() => setConfirmRemove(false)}
          primaryAction={{ content: 'Remove', destructive: true, onAction: onConfirmRemove, loading: isRemoving }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setConfirmRemove(false) }]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              {`Remove ${languageLabel}? Its translations will be permanently deleted. This can't be undone.`}
            </Text>
          </Modal.Section>
        </Modal>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
};

const LanguagesTable = ({ list }: { list: ILanguageList }) => {
  const [showAdd, setShowAdd] = useState(false);
  // Publish gate (caps PUBLISHED non-default languages) — unchanged, used by row actions.
  const publishLimitReached = list.planLimit !== -1 && list.used >= list.planLimit;
  // Add gate — whether the plan still has room to add another non-default language.
  const quota = getAddLanguageQuota(list);

  const addButton = quota.canAdd ? (
    <Button icon={PlusIcon} onClick={() => setShowAdd(true)}>
      Add language
    </Button>
  ) : (
    <Tooltip content={quota.lockReason} preferredPosition="below" dismissOnMouseOut>
      <Button icon={LockIcon} disabled>
        Add language
      </Button>
    </Tooltip>
  );

  return (
    <>
      <CustomCard
        title={
          <div className="d-flex flex-column gap-4">
            Languages
            <Text as="p" variant="bodyMd" tone="subdued">
              {quota.subtitle}
            </Text>
          </div>
        }
        headerStyles={{ alignItems: 'flex-start' }}
        actionGroup={[{ content: addButton }]}
      >
        <IndexTable
          selectable={false}
          itemCount={list.languages.length}
          headings={[{ title: 'Language' }, { title: 'Status' }, { title: 'Last edited' }, { title: '' }]}
        >
          {list.languages.map((item, index) => (
            <LanguageRow key={item.language} item={item} index={index} publishDisabled={publishLimitReached} />
          ))}
        </IndexTable>
      </CustomCard>
      <AddLanguageModal open={showAdd} onClose={() => setShowAdd(false)} list={list} />
    </>
  );
};

export default LanguagesTable;
